import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Archive, Trash2, AlertTriangle, ArchiveRestore } from 'lucide-react';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Danger Zone dialog for a player account.
 *
 * Exposes two operations:
 *   - Archive   → reversible. status = "archived", hidden from Users tab,
 *                 blocks login. Use for duplicates, abandoned accounts.
 *   - Permanently Delete → irreversible nuke. Admin must type the exact name
 *                 to confirm so misclicks can't wipe a real account.
 *
 * Both endpoints refuse to act on admins, self, or accounts with leftover
 * credits/pending cashouts (the backend returns 409 with a clear message).
 */
const DangerZoneDialog = ({ user, onChanged, triggerVariant = 'icon' }) => {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [confirmName, setConfirmName] = useState('');

    const archived = user?.status === 'archived';

    const doArchive = async () => {
        setBusy(true);
        try {
            await axios.post(`${API}/admin/users/${user.id}/archive`);
            toast.success(`${user.name || 'Account'} archived (reversible)`);
            setOpen(false);
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not archive');
        }
        setBusy(false);
    };

    const doRestore = async () => {
        setBusy(true);
        try {
            await axios.post(`${API}/admin/users/${user.id}/restore`);
            toast.success(`${user.name || 'Account'} restored`);
            setOpen(false);
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not restore');
        }
        setBusy(false);
    };

    const doDelete = async () => {
        setBusy(true);
        try {
            await axios.delete(`${API}/admin/users/${user.id}`, {
                data: { confirmation_name: confirmName },
            });
            toast.success(`${user.name || 'Account'} permanently deleted`);
            setOpen(false);
            setConfirmName('');
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not delete');
        }
        setBusy(false);
    };

    const nameMatches = (confirmName || '').trim().toLowerCase() === (user?.name || '').trim().toLowerCase()
        && (user?.name || '').trim() !== '';

    return (
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setConfirmName(''); }}>
            <DialogTrigger asChild>
                {triggerVariant === 'icon' ? (
                    <button
                        type="button"
                        className="text-[#A0AAB2] hover:text-[#FF3B30] p-1"
                        title="Archive or delete account"
                        data-testid={`danger-zone-${user?.id}`}
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                ) : (
                    <Button
                        variant="outline"
                        size="sm"
                        className="border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 h-8 text-xs"
                        data-testid={`danger-zone-${user?.id}`}
                    >
                        <Trash2 className="w-3 h-3 mr-1" /> Delete / Archive
                    </Button>
                )}
            </DialogTrigger>

            <DialogContent className="bg-[#121212] border-white/10 max-w-md">
                <DialogHeader>
                    <DialogTitle className="text-white flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-[#FF8C00]" />
                        Danger Zone — {user?.name || 'this account'}
                    </DialogTitle>
                    <DialogDescription className="text-[#A0AAB2]">
                        Choose Archive (reversible) or Permanently Delete (nuclear).
                    </DialogDescription>
                </DialogHeader>

                {/* Account snapshot */}
                <div className="rounded-lg bg-[#0a0a0a] border border-white/10 p-3 text-xs space-y-0.5">
                    <div className="text-[#A0AAB2]">Phone: <span className="text-white">{user?.phone || '—'}</span></div>
                    <div className="text-[#A0AAB2]">Balance: <span className={`font-mono ${(user?.credits || 0) > 0 ? 'text-[#FFD700]' : 'text-white'}`}>${(user?.credits ?? 0).toFixed(2)}</span></div>
                    <div className="text-[#A0AAB2]">Status: <span className="text-white">{user?.status || 'active'}</span></div>
                </div>

                {(user?.credits || 0) > 0 && (
                    <div className="rounded-lg bg-[#FFD700]/10 border border-[#FFD700]/40 px-3 py-2 text-xs text-[#FFD700]">
                        ⚠️ Player still has credits. Cash out or transfer before deleting.
                    </div>
                )}

                {/* Archive section */}
                {!archived ? (
                    <div className="space-y-2">
                        <h4 className="text-sm font-bold text-white flex items-center gap-2">
                            <Archive className="w-4 h-4 text-[#FFD700]" /> Archive (recommended)
                        </h4>
                        <p className="text-[11px] text-[#A0AAB2]">
                            Hides from Users tab, blocks login, but keeps history. Reversible.
                        </p>
                        <Button
                            onClick={doArchive}
                            disabled={busy}
                            className="w-full bg-[#FFD700] text-black hover:bg-[#FFD700]/80 font-bold h-9"
                            data-testid="danger-archive-btn"
                        >
                            <Archive className="w-4 h-4 mr-1.5" /> Archive Account
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-2">
                        <h4 className="text-sm font-bold text-white flex items-center gap-2">
                            <ArchiveRestore className="w-4 h-4 text-[#39FF14]" /> Restore from archive
                        </h4>
                        <Button
                            onClick={doRestore}
                            disabled={busy}
                            className="w-full bg-[#39FF14] text-black hover:bg-[#39FF14]/80 font-bold h-9"
                            data-testid="danger-restore-btn"
                        >
                            <ArchiveRestore className="w-4 h-4 mr-1.5" /> Restore Account
                        </Button>
                    </div>
                )}

                {/* Hard delete section */}
                <div className="space-y-2 pt-1 border-t border-white/10">
                    <h4 className="text-sm font-bold text-[#FF3B30] flex items-center gap-2">
                        <Trash2 className="w-4 h-4" /> Permanently Delete
                    </h4>
                    <p className="text-[11px] text-[#A0AAB2]">
                        Nuclear option. Removes the account, messages, notifications, password-reset requests, and support chats. Audit log is preserved. <span className="text-[#FF3B30] font-bold">Not reversible.</span>
                    </p>
                    <p className="text-[11px] text-white">
                        Type <span className="font-mono font-bold text-[#FF3B30]">{user?.name}</span> to confirm:
                    </p>
                    <Input
                        value={confirmName}
                        onChange={(e) => setConfirmName(e.target.value)}
                        placeholder={user?.name || ''}
                        className="bg-[#0a0a0a] border-[#FF3B30]/40 text-white"
                        data-testid="danger-confirm-name"
                    />
                    <Button
                        onClick={doDelete}
                        disabled={busy || !nameMatches}
                        variant="outline"
                        className="w-full border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white font-bold h-9 disabled:opacity-40"
                        data-testid="danger-delete-btn"
                    >
                        <Trash2 className="w-4 h-4 mr-1.5" /> Permanently Delete Account
                    </Button>
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setOpen(false)}
                        className="text-[#A0AAB2]"
                    >
                        Cancel
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default DangerZoneDialog;
